import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Crown, Sparkles, Check, Zap, Users, Heart, Star, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { useAnalytics, UpgradeTriggers } from "@/hooks/useAnalytics";

// A/B Test Variants
const PAYWALL_VARIANTS = {
  emotional_dream: {
    id: "emotional_dream",
    headline: "Turn Dreams Into Memories",
    subheadline: "Your bucket list deserves to come alive",
    explorer_pro_cta: "Start Living Your Dreams",
    duo_cta: "Dream Together, Travel Together",
    features_header: "Everything you need to make it real",
    urgency: "Life is short. Adventures are waiting.",
    icon: Heart,
    gradient: "from-rose-500 to-pink-600",
  },
  progress_stats: {
    id: "progress_stats",
    headline: "Unlock Your Full Explorer Potential",
    subheadline: "Track every country, milestone, and achievement",
    explorer_pro_cta: "See Your Complete Stats",
    duo_cta: "Track Progress Together",
    features_header: "Deep insights into your journey",
    urgency: "Don't miss a single milestone.",
    icon: TrendingUp,
    gradient: "from-blue-500 to-indigo-600",
  },
  duo_best_value: {
    id: "duo_best_value",
    headline: "Better Together, Better Price",
    subheadline: "Premium for both of you at one great price",
    explorer_pro_cta: "Go Solo Premium",
    duo_cta: "Get Duo - Best Value",
    features_header: "Everything you both need",
    urgency: "Why pay twice? Upgrade together.",
    savings_callout: "Save £2.52/month vs. 2 separate subscriptions",
    icon: Users,
    gradient: "from-emerald-500 to-teal-600",
  },
  control: {
    id: "control",
    headline: "Upgrade to Premium",
    subheadline: "Unlock all features",
    explorer_pro_cta: "Upgrade to Explorer Pro",
    duo_cta: "Upgrade to Duo",
    features_header: "Premium features",
    urgency: null,
    icon: Crown,
    gradient: "from-primary to-primary/80",
  },
};

// Feature prompts by trigger
const UPGRADE_PROMPTS = {
  ai_suggestions: {
    title: "Unlock AI Trip Suggestions",
    description: "Get personalized trip ideas powered by GPT-5.2, with cost estimates and recommendations tailored to your bucket list.",
    features: ["AI-powered suggestions", "Cost estimates", "Duration & best season", "Personalized recommendations"],
    trigger: UpgradeTriggers.AI_PLANNER,
  },
  budget_tracking: {
    title: "Unlock Budget Tracking",
    description: "Track expenses for all your trips, see spending by category, and stay on budget for your adventures.",
    features: ["Track expenses", "Category breakdown", "Budget vs actual", "Spending insights"],
    trigger: UpgradeTriggers.BUDGET_TRACKING,
  },
  pdf_export: {
    title: "Unlock PDF Export",
    description: "Export your bucket list and trip summaries as beautiful PDFs to print or share.",
    features: ["Export full bucket list", "Trip summary PDFs", "Print-ready format", "Share anywhere"],
    trigger: UpgradeTriggers.PDF_EXPORT,
  },
  public_links: {
    title: "Unlock Public Sharing",
    description: "Generate shareable links for your trips that anyone can view without logging in.",
    features: ["Public trip links", "No login required", "Share on social media", "Beautiful previews"],
    trigger: "public_links",
  },
  groups: {
    title: "Create more Groups",
    description: "You've used your one free group. Upgrade to create unlimited group trips and plan more adventures with friends and family.",
    features: ["Unlimited groups", "Invite up to 50 friends", "Shared bucket lists", "Group activity feed"],
    trigger: "groups",
  },
  // NOTE: `max_items` trigger removed — free tier now has unlimited trips.
  // The 10-item cap proved to be the weakest paywall (the photo / memory /
  // YIR / AI caps drive upgrades more naturally). Kept the photo cap below.
  max_photos_per_item: {
    title: "Unlock Unlimited Photos",
    description: "You've reached the free tier limit of 5 photos per item. Upgrade to add unlimited photos!",
    features: ["Unlimited photos", "High resolution", "Full galleries", "No restrictions"],
    trigger: UpgradeTriggers.PHOTO_LIMIT,
  },
  map_themes: {
    title: "Unlock Exclusive Map Themes",
    description: "Customize your world map with exclusive themes including the Founder's Gold theme.",
    features: ["Exclusive map themes", "Founder's Gold look", "Custom colors", "Premium visuals"],
    trigger: UpgradeTriggers.MAP_LAYERS,
  },
  couple_dashboard: {
    title: "Unlock Couple Dashboard",
    description: "Track your adventures together with a shared dashboard, joint stats, and couple insights.",
    features: ["Joint dashboard", "Shared stats", "Couple milestones", "Travel compatibility"],
    trigger: UpgradeTriggers.PARTNER_LINK_DUO,
    duo_only: true,
  },
  offline_mode: {
    title: "Unlock Offline Mode",
    description: "Access your bucket list anywhere, even without internet. Perfect for remote adventures.",
    features: ["Offline access", "Auto-sync", "No connection needed", "Travel anywhere"],
    trigger: UpgradeTriggers.OFFLINE_MODE,
  },
};

export const UpgradeModal = ({ 
  isOpen, 
  onClose, 
  feature = "ai_suggestions",
  hasPartner = false,
  triggerSource = "locked_feature"
}) => {
  const navigate = useNavigate();
  const { trackPaywallImpression, trackPaywallDismiss, trackConversion, trackMonetization } = useAnalytics();
  
  const [variant, setVariant] = useState(null);
  const [variantCopy, setVariantCopy] = useState(PAYWALL_VARIANTS.control);
  const [loading, setLoading] = useState(false);
  
  const prompt = UPGRADE_PROMPTS[feature] || UPGRADE_PROMPTS.ai_suggestions;
  const isDuoFeature = prompt.duo_only === true;

  useEffect(() => {
    if (isOpen) {
      fetchVariant();
    }
  }, [isOpen]);

  // Fetch A/B variant
  const fetchVariant = async () => {
    try {
      const res = await axios.get(`${API}/monetization/variant`);
      const variantId = res.data.variant;
      setVariant(variantId);
      setVariantCopy(PAYWALL_VARIANTS[variantId] || PAYWALL_VARIANTS.control);
      
      // Track paywall impression with variant
      trackPaywallImpression(variantId, triggerSource, prompt.trigger);
    } catch (error) {
      // Default to control
      setVariant("control");
      setVariantCopy(PAYWALL_VARIANTS.control);
      trackPaywallImpression("control", triggerSource, prompt.trigger);
    }
  };

  const handleUpgrade = (targetPlan = null) => {
    // Track CTA tap
    trackMonetization("cta_tapped", {
      variant,
      triggerSource,
      featureSource: prompt.trigger,
      planId: targetPlan,
    });
    
    onClose();
    navigate(targetPlan === "duo" ? "/pricing?highlight=duo" : "/pricing");
  };

  const handleClose = () => {
    // Track dismiss
    trackPaywallDismiss(variant, triggerSource);
    onClose();
  };

  const IconComponent = variantCopy.icon || Crown;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md bg-card border-none rounded-2xl p-0 overflow-hidden">
        {/* Header with variant-specific gradient */}
        <div className={`bg-gradient-to-br ${variantCopy.gradient} p-8 text-white text-center`}>
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
            <IconComponent className="w-8 h-8" />
          </div>
          <DialogTitle className="font-serif text-2xl mb-2">
            {prompt.title || variantCopy.headline}
          </DialogTitle>
          <DialogDescription className="text-white/80">
            {prompt.description || variantCopy.subheadline}
          </DialogDescription>
          {variantCopy.urgency && (
            <p className="text-white/60 text-sm mt-2 italic">{variantCopy.urgency}</p>
          )}
        </div>

        <div className="p-6">
          {/* Features */}
          <p className="text-sm text-muted-foreground mb-3">{variantCopy.features_header}</p>
          <div className="space-y-3 mb-6">
            {prompt.features.map((feat, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-secondary" />
                </div>
                <span className="text-charcoal">{feat}</span>
              </div>
            ))}
          </div>

          {/* Duo upsell for users with partners */}
          {hasPartner && !isDuoFeature && (
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-emerald-600" />
                <span className="font-semibold text-emerald-800 dark:text-emerald-200">Best Value for Couples!</span>
              </div>
              <p className="text-sm text-emerald-700 dark:text-emerald-300 mb-1">
                Upgrade to Duo and unlock Explorer Pro for both of you.
              </p>
              {variantCopy.savings_callout && (
                <p className="text-xs text-emerald-600 dark:text-emerald-400 mb-3">
                  {variantCopy.savings_callout}
                </p>
              )}
              <Button
                onClick={() => handleUpgrade("duo")}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="duo-upgrade-cta"
              >
                <Users className="w-4 h-4 mr-2" />
                {variantCopy.duo_cta}
              </Button>
            </div>
          )}

          {/* Pricing preview */}
          <div className="bg-accent/10 rounded-xl p-4 mb-6 text-center">
            <p className="text-sm text-muted-foreground mb-1">
              {isDuoFeature ? "Duo" : "Explorer Pro"} starts at
            </p>
            <p className="text-2xl font-bold text-charcoal">
              £{isDuoFeature ? "7.58" : "5.05"}
              <span className="text-base font-normal text-muted-foreground">/month</span>
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              or £{isDuoFeature ? "60.76" : "40.51"}/year (save 2 months)
            </p>
          </div>

          {/* CTAs */}
          <div className="space-y-3">
            <Button
              onClick={() => handleUpgrade(isDuoFeature ? "duo" : null)}
              className={`w-full bg-gradient-to-r ${variantCopy.gradient} text-white hover:opacity-90 h-12`}
              data-testid="upgrade-cta"
            >
              <Zap className="w-4 h-4 mr-2" />
              {isDuoFeature ? variantCopy.duo_cta : variantCopy.explorer_pro_cta}
            </Button>
            <Button
              onClick={handleClose}
              variant="ghost"
              className="w-full text-muted-foreground"
            >
              Maybe later
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeModal;
